import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import {
  Action,
  Alert,
  Card,
  Input,
  MultiSelect,
  Page,
  RadioGroupInput,
  SimpleSelect,
  TextAreaInput,
  TextInput,
  UrlInput,
} from 'corso-admin-ui';
import {
  AlertCategory,
  AlertSeverity,
  AlertTarget,
  commandPlatformApi,
  enumToZodLiteralUnion,
} from 'corso-types';
import { FormEventHandler, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDebounceValue } from 'usehooks-ts';
import { z } from 'zod';
import api from '~/api';
import { usePublishStoreAlert } from '~/hooks/useStoreAlerts';

const createSchema = commandPlatformApi['/store-alert'].post.request.body;

// The form carries the targeting choice flat (`targetKind` + `storeIds`) and
// assembles the discriminated-union `target` at submit; `url` accepts empty.
const formSchema = createSchema
  .omit({ target: true })
  .extend({
    url: z.string().url('Enter a valid URL').or(z.literal('')).optional(),
    targetKind: enumToZodLiteralUnion(AlertTarget),
    storeIds: z.array(z.number()),
  })
  .refine(
    (values) =>
      values.targetKind === AlertTarget.all || values.storeIds.length > 0,
    { message: 'Select at least one store', path: ['storeIds'] },
  );

const severityOptions = Object.values(AlertSeverity).map((value) => ({
  value,
  label: value,
}));

const categoryOptions = Object.values(AlertCategory).map((value) => ({
  value,
  label: value,
}));

const targetLabels = {
  [AlertTarget.all]: 'All stores',
  [AlertTarget.stores]: 'Specific stores',
} satisfies Record<AlertTarget, string>;

export default function AllStoreAlertsPage() {
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      subject: '',
      message: '',
      url: '',
      severity: AlertSeverity.Warning,
      category: AlertCategory.Home,
      targetKind: AlertTarget.all,
      storeIds: [],
    },
  });

  const targetKind = watch('targetKind');

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounceValue(searchTerm, 250);

  const storesQuery = useQuery({
    queryKey: ['stores', { searchTerm: debouncedSearchTerm }],
    queryFn: () =>
      api.stores.list({ searchTerm: debouncedSearchTerm || undefined }),
    enabled: targetKind === AlertTarget.stores,
  });

  const storeOptions = (storesQuery.data ?? []).map((store) => ({
    value: `${store.id}`,
    label: `${store.name} (#${store.id})`,
    keywords: [store.name, `${store.id}`, store.myShopifyDomain],
  }));

  const { mutate: publish, isPending, error } = usePublishStoreAlert();

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit(({ targetKind: kind, storeIds, url, ...content }) => {
      publish(
        {
          ...content,
          url: url || null,
          target:
            kind === AlertTarget.all
              ? { kind: AlertTarget.all }
              : { kind: AlertTarget.stores, storeIds },
        },
        {
          onSuccess: () => {
            reset();
            setSearchTerm('');
          },
        },
      );
    })(event).catch(console.error);
  };

  return (
    <Page title="Store Alerts">
      <Card>
        <form className="flex flex-col gap-6" onSubmit={submitHandler}>
          <TextInput
            id="subject"
            label="Subject"
            required
            placeholder="EasyPost outage"
            {...register('subject')}
            error={errors.subject?.message}
          />
          <TextAreaInput
            id="message"
            label="Message"
            required
            placeholder="EasyPost is down, you may have issues generating labels."
            {...register('message')}
            error={errors.message?.message}
          />
          <UrlInput
            id="url"
            label="Link"
            details="Optional link included with the alert."
            {...register('url')}
            error={errors.url?.message}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Controller
              control={control}
              name="severity"
              render={({ field, fieldState }) => (
                <SimpleSelect
                  label="Severity"
                  required
                  options={severityOptions}
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="category"
              render={({ field, fieldState }) => (
                <SimpleSelect
                  label="Category"
                  required
                  options={categoryOptions}
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>

          <Controller
            control={control}
            name="targetKind"
            render={({ field }) => (
              <RadioGroupInput
                id="targetKind"
                label="Publish to"
                value={field.value}
                onChange={field.onChange}
                options={targetLabels}
              />
            )}
          />

          {targetKind === AlertTarget.stores ? (
            <div className="flex flex-col gap-2">
              <Input
                id="store-search"
                label="Search stores"
                type="search"
                placeholder="Search by name, id or domain"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
              <Controller
                control={control}
                name="storeIds"
                render={({ field, fieldState }) => (
                  <MultiSelect
                    label="Selected stores"
                    placeholder="Select stores"
                    options={storeOptions}
                    value={field.value.map(
                      (id) =>
                        storeOptions.find(
                          (option) => option.value === `${id}`,
                        ) ?? {
                          value: `${id}`,
                          label: `#${id}`,
                        },
                    )}
                    onChange={(selected) =>
                      field.onChange(
                        selected.map((option) =>
                          Number.parseInt(option.value, 10),
                        ),
                      )
                    }
                    error={fieldState.error?.message}
                    clearable
                  />
                )}
              />
            </div>
          ) : null}

          {error ? (
            <Alert heading="Failed to Publish Alert" variant="critical">
              {error.message}
            </Alert>
          ) : null}

          <Action
            variant="primary"
            type="submit"
            loading={isPending}
            disabled={isPending}
          >
            Publish alert
          </Action>
        </form>
      </Card>
    </Page>
  );
}
